import {
    ButtonType,
    ButtonWidget,
    TextLinkWidget
} from '@tableau/widgets';
import React, { ReactElement, useCallback, useState } from 'react';

import { Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { ISiteTrial, TrialStatus } from '~src/siteManagement/serverApi/types/ISiteTrial';

import { agents } from '../tableau-ai-agents/Agents';
import { getTestId } from '../tableau-ai-agents/AgentSectionsUtils';
import { TryTableauAIConfirmationDialog } from './TryTableauAIConfirmationDialog';

// Delimiter for splitting the legal disclaimer text into two parts.
const LINK_PLACEHOLDER = '\u200B\u200B\u200B';
const MAIN_SERVICES_AGREEMENT_URL = 'https://www.salesforce.com/company/legal/';

function calculateDaysRemaining(expiresAtIso: string): number {
    const MS_PER_DAY = 1000 * 60 * 60 * 24;

    const today = new Date();
    const expiryDate = new Date(expiresAtIso);
    const differenceInTime = expiryDate.getTime() - today.getTime();
    return Math.max(Math.ceil(differenceInTime / MS_PER_DAY), 0);
}

interface TryTableauAISectionProps {
    onActivate: () => void;
    tableauAITrial?: ISiteTrial;
}

export function TryTableauAISection({ onActivate, tableauAITrial }: TryTableauAISectionProps): ReactElement {
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    // Button is disabled when a Tableau AI trial exists, unless that trial can be restarted
    // (expired with newly-onboarded features). A restartable trial re-enables the button.
    const canRestartTrial = !!tableauAITrial?.canRestart;
    const isButtonDisabled = tableauAITrial != null && !canRestartTrial;
    const isTableauAIExpired = tableauAITrial?.status === TrialStatus.EXPIRED;
    const daysRemaining =
        tableauAITrial?.expiresAt != null ? calculateDaysRemaining(tableauAITrial.expiresAt) : undefined;

    const legalDisclaimerParts = Messages.settings_agents_try_tableau_ai_legal_disclaimer({ link: LINK_PLACEHOLDER }).split(LINK_PLACEHOLDER);
    const mainServicesAgreementLink = (
        <TextLinkWidget
            href={MAIN_SERVICES_AGREEMENT_URL}
            target="_blank"
            testId={getTestId('main_services_agreement_link')}
        >
            {Messages.settings_agents_try_tableau_ai_legal_link_text()}
        </TextLinkWidget>
    );

    function getButtonText(): string {
        if (canRestartTrial) {
            return Messages.settings_agents_try_tableau_ai_restart_button();
        }
        if (isButtonDisabled) {
            if (isTableauAIExpired) {
                return Messages.settings_agents_try_tableau_ai_start_button_expired();
            }
            return Messages.settings_agents_try_tableau_ai_start_button_active({ N: String(daysRemaining ?? 0) });
        }
        return Messages.settings_agents_try_tableau_ai_start_button();
    }

    const handleActivate = useCallback((): void => {
        setIsDialogOpen(false);
        onActivate();
    }, [onActivate]);

    // restartFeatures is read directly off the trial object — the backend computes it and the UI
    // does not derive it. It lists the features not covered by the current trial that would only
    // become available on restart, so until then they are not part of what this trial includes and
    // must not appear in the list unless the trial is restartable.
    const restartFeatures = tableauAITrial?.restartFeatures ?? [];

    // The trial only gates features that require Salesforce registration. Agents that are always
    // enabled (requiresRegistration === false, e.g. Tableau Pulse) are not part of the "Try it"
    // trial, so they are excluded from this list. The agent.flag check mirrors AgentRow's row-hiding
    // gate, so a feature that is not shown in the permissions table below is not shown here either.
    const trialFeatures = agents
        .filter(agent =>
            agent.availableInTableauAITrial
            && agent.requiresRegistration
            && (!agent.flag || FeatureFlags[agent.flag])
            // Hide restart-only features until the trial can actually be restarted.
            && (canRestartTrial || !restartFeatures.includes(agent.enabledSiteSetting)))
        .map(agent => ({
            agent,
            isNew: canRestartTrial && restartFeatures.includes(agent.enabledSiteSetting),
            name: agent.displayName(),
        }))
        .sort((a, b) => Number(!!a.isNew) - Number(!!b.isNew));

    return (
        <div id="try-tableau-ai-section">
            <p>
                <b>{Messages.settings_agents_try_tableau_ai_header()}</b>
            </p>
            <p> {Messages.settings_agents_try_tableau_ai_description()}
                <TextLinkWidget href={HelpService.getHelpUrlByCategory('gpt-salesforce-login')} target="_blank" testId={getTestId('ai_help_link_learn_more')}>
                    {Messages.learnMore()}
                </TextLinkWidget>
            </p>
            <p> {Messages.settings_agents_try_tableau_ai_features_list_title()} </p>
            <ul id="try-tableau-ai-features-list">
                {trialFeatures.map(({ agent, name, isNew }) => (
                    <li key={agent.id} id={`try-tableau-ai-features-list-item-${agent.id}`}>
                        {name}{isNew && <> <b>{Messages.settings_agents_try_tableau_ai_features_list_new()}</b></>}
                    </li>
                ))}
            </ul>
            {canRestartTrial && (
                <p id="try-tableau-ai-restart-message">
                    {Messages.settings_agents_try_tableau_ai_restart_message()}
                </p>
            )}
            <p>
                {legalDisclaimerParts[0]}{mainServicesAgreementLink}{legalDisclaimerParts[1]}
            </p>
            <ButtonWidget
                buttonType={ButtonType.Primary}
                testId='try-tableau-ai-start-trial'
                type="button"
                disabled={isButtonDisabled}
                onClick={() => setIsDialogOpen(true)}
            >
                {getButtonText()}
            </ButtonWidget>
            {isDialogOpen && <TryTableauAIConfirmationDialog isRestart={canRestartTrial} onActivate={handleActivate} onClose={() => setIsDialogOpen(false)} />}
        </div>
    );
}
